import React, { useState, useEffect, useCallback, useRef } from "react";
import styled from "styled-components";
import { useSecureChat } from "./SecureChatContext";
import MessageHeader from "./MessageHeader";
import MessageList from "./MessageList";
import MessageInput from "./MessageInput";
const api = require("../../api/secureMessageApi");
const messageHandler = require("../../utils/secure/messageHandler");
const socketClient = require("../../api/secureSocket");

const MessageArea = ({ onClose }) => {
  const {
    selectedChatId,
    chats,
    messages,
    currentUser,
    loading,
    error,
    setMessages,
    addMessage,
    updateMessage,
    setLoading,
    setError,
    setTypingUser,
    updateChat,
  } = useSecureChat();

  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const messagesEndRef = useRef(null);
  const messageListRef = useRef(null);
  const readDebounceRef = useRef(null);

  // Get current chat (normalize chats to array)
  const chatsArray = Array.isArray(chats) ? chats : [];
  const currentChat = chatsArray.find((chat) => chat._id === selectedChatId);
  const currentUnreadCount = currentChat?.unreadCount ?? 0;

  // Check if message can be edited/deleted
  const canEditDelete = useCallback(
    (message) => {
      if (!message || !currentUser) return false;
      const senderId = message.sender?._id || message.sender;
      if (senderId !== currentUser._id) return false;
      if (message.isDeleted) return false;
      return true;
    },
    [currentUser],
  );

  // Fetch messages for the selected chat
  const fetchMessages = useCallback(
    async (page = 1, append = false) => {
      if (!selectedChatId) return;

      try {
        if (page === 1) {
          setLoading("messages", true);
        } else {
          setIsLoadingMore(true);
        }
        setError(null);

        const response = await api.getMessages(selectedChatId, {
          page,
          limit: 50,
        });
        const messagesData = response?.data || response || [];

        if (messagesData.length === 0) {
          setHasMoreMessages(false);
          return;
        }

        // Decrypt messages
        const decryptedMessages = await messageHandler.decryptMessages(
          messagesData,
          selectedChatId,
        );

        if (append) {
          // Avoid functional updater; context setMessages expects an array payload
          setMessages([...decryptedMessages, ...messages]);
        } else {
          setMessages(decryptedMessages);
        }

        // Check if there are more messages
        if (messagesData.length < 50) {
          setHasMoreMessages(false);
        }
      } catch (error) {
        console.error("Error fetching messages:", error);
        setError("Failed to load messages");
      } finally {
        setLoading("messages", false);
        setIsLoadingMore(false);
      }
    },
    [selectedChatId, setMessages, setLoading, setError],
  );

  // Load messages when chat changes
  useEffect(() => {
    if (selectedChatId) {
      setCurrentPage(1);
      setHasMoreMessages(true);
      fetchMessages(1, false);
    } else {
      setMessages([]);
    }
  }, [selectedChatId, setMessages]);

  useEffect(() => {
    if (selectedChatId && currentUnreadCount > 0) {
      updateChat(selectedChatId, { unreadCount: 0 });
    }
  }, [selectedChatId, currentUnreadCount, updateChat]);

  // Join socket room when chat changes
  useEffect(() => {
    if (selectedChatId) {
      socketClient.joinSecureChat(selectedChatId);
    }
  }, [selectedChatId]);

  // Set up socket listeners
  useEffect(() => {
    if (!selectedChatId) return;

    // Message listener
    const handleSecureMessage = (data) => {
      console.log("Received secure message:", data);
      if (data?.chat?._id === selectedChatId) {
        // Prevent duplicate echo for the sender and skip duplicates by _id
        const incomingSenderId = data?.sender?._id || data?.sender;
        if (
          incomingSenderId &&
          currentUser?._id &&
          incomingSenderId === currentUser._id
        ) {
          return;
        }
        if (
          Array.isArray(messages) &&
          data?._id &&
          messages.some((m) => m._id === data._id)
        ) {
          return;
        }
        // Decrypt the message
        messageHandler
          .decryptMessageObject(data, selectedChatId)
          .then((decryptedMessage) => {
            addMessage(decryptedMessage);
          })
          .catch((error) => {
            console.error("Error decrypting received message:", error);
            // Add message with error
            addMessage({
              ...data,
              decryptedContent: "Failed to decrypt message",
              decryptError: true,
            });
          });
      }
    };

    // Typing listener
    const handleTyping = (data) => {
      if (data?.chatId === selectedChatId) {
        setTypingUser(selectedChatId, data.userId, data.isTyping);
      }
    };

    // Read receipt listener
    const handleMessagesRead = (data) => {
      if (data?.chatId === selectedChatId) {
        // Update message read status in local state
        const readerId = data.userId;
        if (!readerId) return;

        // If specific messageIds provided, update only those; otherwise update all
        const idsToUpdate = Array.isArray(data.messageIds)
          ? data.messageIds
          : null;
        // Avoid functional updater; use current messages
        setMessages(
          messages.map((m) => {
            const alreadyRead =
              Array.isArray(m.readBy) && m.readBy.includes(readerId);
            const shouldUpdate = idsToUpdate
              ? idsToUpdate.includes(m._id)
              : true;
            if (alreadyRead || !shouldUpdate) return m;
            return { ...m, readBy: [...(m.readBy || []), readerId] };
          }),
        );

        if (readerId === currentUser?._id) {
          updateChat(selectedChatId, { unreadCount: 0 });
        }
      }
    };

    // Message edited listener
    const handleMessageEdited = (data) => {
      if (data?.chatId === selectedChatId && data?.messageId) {
        // Decrypt the edited message content
        messageHandler
          .decryptMessageObject(
            {
              content: data.content,
              iv: data.iv,
              authTag: data.authTag,
              _id: data.messageId,
            },
            selectedChatId,
          )
          .then((decryptedMessage) => {
            // Update message in context
            updateMessage(data.messageId, {
              content: data.content,
              iv: data.iv,
              authTag: data.authTag,
              editedAt: data.editedAt,
              isEdited: data?.isEdited ?? true,
              decryptedContent: decryptedMessage.decryptedContent,
            });
          })
          .catch((error) => {
            console.error("Error decrypting edited message:", error);
            // Update with encrypted content if decryption fails
            updateMessage(data.messageId, {
              content: data.content,
              iv: data.iv,
              authTag: data.authTag,
              editedAt: data.editedAt,
              isEdited: data?.isEdited ?? true,
              decryptedContent: "Failed to decrypt edited message",
              decryptError: true,
            });
          });
      }
    };

    // Message deleted listener
    const handleMessageDeleted = (data) => {
      if (data?.chatId === selectedChatId && data?.messageId) {
        // Update message in context
        updateMessage(data.messageId, {
          isDeleted: true,
          deletedAt: data.deletedAt,
        });
      }
    };

    // Set up listeners and capture unsubscribe callbacks
    const unsubMsg = socketClient.onSecureMessage(handleSecureMessage);
    const unsubTyping = socketClient.onTyping(handleTyping);
    const unsubRead = socketClient.onMessagesRead(handleMessagesRead);
    const unsubEdited = socketClient.onMessageEdited(handleMessageEdited);
    const unsubDeleted = socketClient.onMessageDeleted(handleMessageDeleted);

    // Cleanup using the unsubscribe callbacks to avoid handler leaks
    return () => {
      try {
        unsubMsg && unsubMsg();
      } catch (_e) {}
      try {
        unsubTyping && unsubTyping();
      } catch (_e) {}
      try {
        unsubRead && unsubRead();
      } catch (_e) {}
      try {
        unsubEdited && unsubEdited();
      } catch (_e) {}
      try {
        unsubDeleted && unsubDeleted();
      } catch (_e) {}
    };
  }, [
    selectedChatId,
    addMessage,
    setTypingUser,
    currentUser?._id,
    messages,
    setMessages,
    updateChat,
  ]);

  // Automatically mark visible/unread messages as read when messages update
  useEffect(() => {
    if (
      !selectedChatId ||
      !currentUser?._id ||
      !Array.isArray(messages) ||
      messages.length === 0
    ) {
      return;
    }

    // Debounce to avoid rapid API calls on quick successive updates
    if (readDebounceRef.current) clearTimeout(readDebounceRef.current);
    readDebounceRef.current = setTimeout(async () => {
      try {
        // Collect unread messages not sent by the current user
        const unreadMessageIds = messages
          .filter(
            (m) =>
              (m?.sender?._id || m?.sender) !== currentUser._id &&
              !(Array.isArray(m.readBy) && m.readBy.includes(currentUser._id)),
          )
          .map((m) => m._id)
          .filter(Boolean);

        if (unreadMessageIds.length === 0) return;

        // Persist read state via API
        try {
          await messageHandler.markMessagesRead(
            selectedChatId,
            unreadMessageIds,
          );
        } catch (_e) {
          // Non-blocking; still update UI optimistically
        }

        // Update local state optimistically; avoid functional updater
        setMessages(
          messages.map((m) =>
            unreadMessageIds.includes(m._id)
              ? { ...m, readBy: [...(m.readBy || []), currentUser._id] }
              : m,
          ),
        );

        updateChat(selectedChatId, { unreadCount: 0 });

        // Notify others via socket
        try {
          socketClient.emitMessagesRead(selectedChatId, currentUser._id);
        } catch (_e) {}
      } catch (_e) {}
    }, 150);

    return () => {
      if (readDebounceRef.current) clearTimeout(readDebounceRef.current);
    };
  }, [messages, selectedChatId, currentUser?._id, setMessages, updateChat]);

  // Handle edit message
  const handleEditMessage = useCallback(
    (message) => {
      if (canEditDelete(message)) {
        setEditingMessage(message);
      }
    },
    [canEditDelete],
  );

  // Handle edit submit
  const handleEditSubmit = useCallback(
    (editedMessage) => {
      // Prefer decryptedContent from response; otherwise fallback to previous message text
      const previous = Array.isArray(messages)
        ? messages.find((m) => m._id === editedMessage._id)
        : null;
      updateMessage(editedMessage._id, {
        content: editedMessage.content,
        iv: editedMessage.iv,
        authTag: editedMessage.authTag,
        editedAt: editedMessage.editedAt,
        isEdited: true,
        decryptedContent:
          editedMessage.decryptedContent || previous?.decryptedContent || "",
      });
      setEditingMessage(null);
    },
    [updateMessage, messages],
  );

  // Handle edit cancel
  const handleEditCancel = useCallback(() => {
    setEditingMessage(null);
  }, []);

  // Handle delete message
  const handleDeleteMessage = useCallback(
    async (message) => {
      if (!canEditDelete(message)) return;

      if (window.confirm("Are you sure you want to delete this message?")) {
        try {
          // Call API to delete message
          await messageHandler.deleteMessage(message._id);

          // Update message in context
          updateMessage(message._id, {
            isDeleted: true,
            deletedAt: new Date().toISOString(),
          });

          // Emit deleted message over socket
          try {
            const senderObject = {
              _id: currentUser._id,
              name: currentUser.name,
              profilePic: currentUser.profilePic,
            };
            socketClient.emitMessageDeleted(
              selectedChatId,
              message._id,
              new Date().toISOString(),
              senderObject,
            );
          } catch (_e) {}
        } catch (error) {
          console.error("Error deleting message:", error);
          // You could add a toast notification here
        }
      }
    },
    [canEditDelete, updateMessage, currentUser, selectedChatId],
  );

  // Load more messages when scrolling to top
  const handleLoadMore = useCallback(() => {
    if (!isLoadingMore && hasMoreMessages) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchMessages(nextPage, true);
    }
  }, [currentPage, isLoadingMore, hasMoreMessages, fetchMessages]);

  // Scroll to bottom
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages.length, scrollToBottom]);

  if (!selectedChatId) {
    return (
      <EmptyContainer>
        <EmptyIcon>💬</EmptyIcon>
        <EmptyTitle>Select a chat to start messaging</EmptyTitle>
        <EmptySubtitle>
          Choose a conversation from the list to begin
        </EmptySubtitle>
      </EmptyContainer>
    );
  }

  if (!currentChat) {
    if (loading?.chats) {
      return (
        <LoadingContainer>
          <LoadingText>Loading chat...</LoadingText>
        </LoadingContainer>
      );
    }
    return (
      <ErrorContainer>
        <ErrorText>Chat not found</ErrorText>
        <RetryButton onClick={() => window.location.reload()}>
          Retry
        </RetryButton>
      </ErrorContainer>
    );
  }

  if (loading.messages) {
    return (
      <LoadingContainer>
        <LoadingText>Loading messages...</LoadingText>
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <ErrorContainer>
        <ErrorText>{error}</ErrorText>
        <RetryButton onClick={() => fetchMessages(1, false)}>Retry</RetryButton>
      </ErrorContainer>
    );
  }

  return (
    <Container>
      <MessageHeader chat={currentChat} onClose={onClose} />

      <MessageList
        ref={messageListRef}
        messages={messages}
        onLoadMore={handleLoadMore}
        hasMore={hasMoreMessages}
        isLoadingMore={isLoadingMore}
        onEdit={handleEditMessage}
        onDelete={handleDeleteMessage}
        canEdit={canEditDelete}
        canDelete={canEditDelete}
      />

      <MessageInput
        chatId={selectedChatId}
        onMessageSent={scrollToBottom}
        editingMessage={editingMessage}
        onEditSubmit={handleEditSubmit}
        onEditCancel={handleEditCancel}
      />

      <div ref={messagesEndRef} />
    </Container>
  );
};

export default MessageArea;

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: white;
  border-right: 2px solid var(--color-border);
`;

const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 40px 20px;
  text-align: center;
  background-color: transparent;
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.5;
  color: var(--color-text);
`;

const EmptyTitle = styled.h3`
  margin: 0 0 8px 0;
  font-size: 20px;
  color: var(--color-text);
  font-weight: 700;
  font-family: "Merriweather", serif;
`;

const EmptySubtitle = styled.p`
  margin: 0;
  color: var(--color-text-secondary);
  font-size: 16px;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 40px 20px;
  text-align: center;
  background-color: transparent;
`;

const ErrorText = styled.p`
  margin: 0 0 16px 0;
  color: var(--color-text);
  font-size: 16px;
`;

const RetryButton = styled.button`
  background-color: transparent;
  color: var(--color-text);
  border: 1px solid var(--color-border);
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    background-color: var(--color-surface);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 40px 20px;
  background-color: transparent;
`;

const LoadingText = styled.p`
  margin: 0;
  color: var(--color-text);
  font-size: 16px;
`;
